import { FC, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useRole } from '../../hooks/useRole';

const SpinTheWheelPage: FC = () => {
  const { isAdmin, loading } = useRole();
  const [activityName, setActivityName] = useState('');
  const [participantsText, setParticipantsText] = useState('');
  const [spinCount, setSpinCount] = useState<number>(1);
  const [participantsPool, setParticipantsPool] = useState<string[]>([]);
  const [winners, setWinners] = useState<string[]>([]);
  const [currentWinner, setCurrentWinner] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const spinTimeoutRef = useRef<number | null>(null);
  const wheelRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    return () => {
      if (spinTimeoutRef.current !== null) {
        clearTimeout(spinTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleInitialize = () => {
    const rawNames = participantsText
      .split(/[\n,]+/)
      .map((name) => name.trim())
      .filter((name) => name.length > 0);

    const uniqueNames = Array.from(new Set(rawNames));

    setParticipantsPool(uniqueNames);
    setWinners([]);
    setCurrentWinner(null);
    setInitialized(true);
    setRotation(0);
    setSpinning(false);
    if (spinTimeoutRef.current !== null) {
      clearTimeout(spinTimeoutRef.current);
      spinTimeoutRef.current = null;
    }
  };

  const handleSpin = () => {
    if (!initialized) return;
    if (spinning) return;
    if (participantsPool.length === 0) return;
    if (winners.length >= spinCount) return;

    const currentPool = participantsPool;
    const segmentAngleLocal = 360 / currentPool.length;
    
    // Pick a random winner
    const winnerIndex = Math.floor(Math.random() * currentPool.length);
    const winner = currentPool[winnerIndex];
    
    // Calculate the angle where this winner's segment center is located
    // Segments start at -90 degrees (top), so segment 0 center is at -90 + segmentAngle/2
    const winnerSegmentCenterAngle = winnerIndex * segmentAngleLocal + segmentAngleLocal / 2;
    
    // We want the pointer (at top = 0 degrees in our display, which is -90 in math coords) 
    // to point at the winner's segment center
    // So we need to rotate the wheel so that winner's segment is at the top
    // Target rotation = -(winnerSegmentCenterAngle - 90) to bring it to top
    const targetAngle = -(winnerSegmentCenterAngle - 90);
    
    // Add multiple spins (4-6 full rotations) for effect
    const fullSpins = 4 + Math.floor(Math.random() * 3);
    const newRotation = rotation + fullSpins * 360 + (targetAngle - (rotation % 360));

    setSpinning(true);
    setRotation(newRotation);

    if (spinTimeoutRef.current !== null) {
      clearTimeout(spinTimeoutRef.current);
    }

    spinTimeoutRef.current = window.setTimeout(() => {
      setCurrentWinner(winner);
      setWinners((prev) => [...prev, winner]);
      setParticipantsPool((prev) => prev.filter((name) => name !== winner));
      setSpinning(false);
      spinTimeoutRef.current = null;
    }, 4000);
  };

  const totalPlannedSpins = spinCount > 0 ? spinCount : 0;
  const completedSpins = winners.length;
  const maxPossibleSpins = winners.length + participantsPool.length;
  const effectivePlannedSpins = totalPlannedSpins > 0 ? Math.min(totalPlannedSpins, maxPossibleSpins) : maxPossibleSpins;
  const remainingSpins = Math.max(0, effectivePlannedSpins - completedSpins);

  const activeSegments = participantsPool.length > 0 ? participantsPool : [];
  const segmentCount = activeSegments.length || 1;
  const segmentAngle = 360 / segmentCount;
  const baseColors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#0ea5e9', '#6366f1', '#a855f7', '#ec4899'];

  const canSpin =
    initialized &&
    !spinning &&
    activeSegments.length > 0 &&
    completedSpins < totalPlannedSpins;

  // Helper function to create SVG path for a wheel segment
  const createSegmentPath = (index: number, radius: number) => {
    const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
    const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);
    
    const x1 = radius + radius * Math.cos(startAngle);
    const y1 = radius + radius * Math.sin(startAngle);
    const x2 = radius + radius * Math.cos(endAngle);
    const y2 = radius + radius * Math.sin(endAngle);
    
    const largeArcFlag = segmentAngle > 180 ? 1 : 0;
    
    return `M ${radius} ${radius} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  };

  // Calculate text position for each segment
  const getTextPosition = (index: number, radius: number) => {
    const angle = (index * segmentAngle + segmentAngle / 2 - 90) * (Math.PI / 180);
    const textRadius = radius * 0.7; // Position text 70% from center
    const x = radius + textRadius * Math.cos(angle);
    const y = radius + textRadius * Math.sin(angle);
    const rotation = index * segmentAngle + segmentAngle / 2;
    
    return { x, y, rotation };
  };

  const handleSpinCountChange = (value: string) => {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed <= 0) {
      setSpinCount(1);
    } else {
      setSpinCount(parsed);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Spin the Wheel</h1>
        <p className="text-gray-600">
          Enter an activity, list of participants, and number of spins. Each spin selects a unique random winner.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Activity Name
            </label>
            <input
              type="text"
              value={activityName}
              onChange={(e) => setActivityName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Year-End Raffle, Team Building Raffle"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Spins
              </label>
              <input
                type="number"
                min={1}
                value={spinCount}
                onChange={(e) => handleSpinCountChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-500">
                Spins completed: {completedSpins}/{totalPlannedSpins || '-'}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Participants
            </label>
            <textarea
              value={participantsText}
              onChange={(e) => setParticipantsText(e.target.value)}
              rows={8}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter one name per line or separate names with commas"
            />
            <div className="mt-2 text-xs text-gray-500">
              Duplicate and empty names are automatically cleaned up when initializing the game.
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={handleInitialize}
              disabled={!participantsText.trim()}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition font-medium"
            >
              Initialize Game
            </button>
            <div className="text-sm text-gray-500 flex items-center">
              Participants loaded: {participantsPool.length + winners.length}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center justify-between gap-4">
          <div className="w-full text-center">
            <div className="text-sm font-medium text-gray-500 mb-3">
              {activityName || 'No activity name set'}
            </div>
            <div className="relative w-80 h-80 mx-auto mb-4">
              {/* Pointer - Enhanced and more prominent */}
              <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center">
                <div className="relative">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 border-[5px] border-white shadow-2xl flex items-center justify-center animate-pulse">
                    <div className="w-6 h-6 rounded-full bg-white shadow-lg" />
                  </div>
                  {/* Glow effect */}
                  <div className="absolute inset-0 rounded-full bg-amber-400 opacity-50 blur-md -z-10" />
                </div>
                {/* Arrow pointing down */}
                <div className="relative -mt-2">
                  <div className="w-0 h-0 border-l-[14px] border-r-[14px] border-t-[24px] border-l-transparent border-r-transparent border-t-amber-500 drop-shadow-2xl" />
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[10px] border-r-[10px] border-t-[18px] border-l-transparent border-r-transparent border-t-amber-300" />
                </div>
              </div>

              {/* SVG Wheel */}
              <div className="relative w-full h-full">
                <svg
                  ref={wheelRef}
                  viewBox="0 0 400 400"
                  className="w-full h-full transition-transform duration-[4000ms] ease-[cubic-bezier(0.17,0.67,0.27,1.35)]"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))',
                  }}
                >
                  {/* Define gradients once at the top */}
                  <defs>
                    <radialGradient id="emptyGradient">
                      <stop offset="0%" stopColor="#1f2937" />
                      <stop offset="100%" stopColor="#020617" />
                    </radialGradient>
                    <radialGradient id="centerGradient">
                      <stop offset="0%" stopColor="#334155" />
                      <stop offset="100%" stopColor="#1e293b" />
                    </radialGradient>
                  </defs>

                  {/* Outer ring decorative border */}
                  <circle cx="200" cy="200" r="198" fill="none" stroke="#f59e0b" strokeWidth="8" />
                  <circle cx="200" cy="200" r="190" fill="none" stroke="#fbbf24" strokeWidth="2" />

                  {/* Wheel segments */}
                  {activeSegments.length > 0 ? (
                    activeSegments.map((name, index) => {
                      const color = baseColors[index % baseColors.length];
                      const path = createSegmentPath(index, 185);
                      const textPos = getTextPosition(index, 200);
                      
                      return (
                        <g key={name + index}>
                          {/* Segment slice */}
                          <path d={path} fill={color} stroke="#ffffff" strokeWidth="3" />
                          
                          {/* Participant name */}
                          <text
                            x={textPos.x}
                            y={textPos.y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            transform={`rotate(${textPos.rotation}, ${textPos.x}, ${textPos.y})`}
                            fill="white"
                            fontWeight="bold"
                            fontSize="16"
                            style={{
                              paintOrder: 'stroke fill',
                              stroke: '#000',
                              strokeWidth: '3px',
                              strokeLinecap: 'round',
                              strokeLinejoin: 'round',
                            }}
                          >
                            {name.length > 12 ? name.substring(0, 10) + '...' : name}
                          </text>
                        </g>
                      );
                    })
                  ) : (
                    <circle cx="200" cy="200" r="185" fill="url(#emptyGradient)" />
                  )}

                  {/* Center circle - always on top */}
                  <circle cx="200" cy="200" r="55" fill="#1e293b" stroke="#94a3b8" strokeWidth="5" />
                  <circle cx="200" cy="200" r="50" fill="url(#centerGradient)" />
                </svg>

                {/* Current winner display - positioned absolutely to always stay centered */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center px-2" style={{ maxWidth: '90px' }}>
                    <div className="text-[10px] font-bold text-slate-300 uppercase tracking-wider mb-1">
                      {spinning ? 'Spinning' : 'Winner'}
                    </div>
                    <div className="text-xs font-bold text-white leading-tight">
                      {currentWinner ? (currentWinner.length > 10 ? currentWinner.substring(0, 8) + '...' : currentWinner) : '—'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-1">
              Remaining participants: {participantsPool.length}
            </div>
            <div className="text-xs text-gray-500">
              Remaining spins (up to planned): {remainingSpins}
            </div>
          </div>

          <button
            type="button"
            onClick={handleSpin}
            disabled={!canSpin}
            className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all font-bold text-sm uppercase tracking-wide shadow-lg"
          >
            {spinning ? '🎰 Spinning...' : '🎯 Spin the Wheel'}
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Remaining Participants</h2>
          {participantsPool.length === 0 ? (
            <p className="text-sm text-gray-500">No remaining participants.</p>
          ) : (
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {participantsPool.map((name, index) => (
                <li key={name + index} className="flex items-center justify-between text-sm text-gray-800">
                  <span>{name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Winners</h2>
          {winners.length === 0 ? (
            <p className="text-sm text-gray-500">No winners yet.</p>
          ) : (
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {winners.map((name, index) => (
                <li key={name + index} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-800">{name}</span>
                  <span className="text-xs text-gray-500">
                    Winner #{index + 1}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpinTheWheelPage;
